import { Component, createRef } from 'react';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ExtensionInfoComponent } from '../../components/ui/extensions-info/extensions-info.component';
import { FeatureDetailsComponent } from '../../components/ui/feature-details/feature-details.component';
import FeaturesTilesComponent from '../../components/ui/features-tiles/features-tiles.component';
import { MotiveComponent } from '../../components/ui/motive/motive.component';
import { AdSlots } from '../ui/motive/ad-slots';
import RatingDisplayComponent from '../../components/ui/rating-display/rating-display.component';
import { RelatedLinksComponent } from '../../components/ui/related-links/related-links.component';
import { IOC_TYPES } from '../../context/ioc.types';
import { lazyInject } from '../../context/module';
import { FileUpload } from '../../models/file-upload';
import { Logger } from '../../models/logger';
import { DOMStorage } from '../../services/dom-storage.service';
import { EnvironmentService } from '../../services/environment.service';
import { LightHttp } from '../../services/light-http.service';
import { MotivesService } from '../../services/motives.service';
import { PageDataProviderService } from '../../services/page-data-provider.service';
import { SettingsService } from '../../services/settings.service';
import { UploadedFilesService } from '../../services/uploaded-files.service';
import { WindowService } from '../../services/window.service';
import { ExtensionSEOData, PageCallout, PageData, RouteProps, SeoFeature } from '../../types/route-types';
import { pageCalloutOf } from '../../utils/page-callout';
import { TextWithLink } from '../ui/unsupported-input/text-with-link';
import { FromType, FromTypes, ToType, ToTypes } from '../../types/types';
import { BrowserUtils } from '../../ui-utils/browser-utils';
import { get } from '../../utils/lodash-min';
import { MotiveConfig, MotiveLocationTypes } from '../../utils/request.utils';
import { RouteUtils } from '../../utils/route.utils';
import { untilDestroyed } from '../../utils/until-destroyed';
import MotiveFrame from '../motive-frames/motive-frame.component';
import styles from './converter-page-base.component.module.scss';
import FileConvertComponent from './file-convert.component';
import dynamic from 'next/dynamic';
import { TabStripComponent } from '../ui/tab-strip/tab-strip.component';
import type { TabDefinition } from '../ui/tab-strip/tab-strip.component';
import {
    PICKER_TOP_VAR, WORKSPACE_BODY_CLASS, fittedCardStyle, fittedWrapperStyle, isWorkspaceActive,
    publishFittedTop, showPageFurniture, syncEditorSnap, workspaceWrapperStyle,
} from '../../utils/workspace-mode';
import { onRouteChange, writeTabParam } from '../../utils/route-state';
import { closeStudioTab, isStudioTab, readStudioUrl } from '../studio/state/studio-url';
import {
    CONVERT_TAB, CONVERT_TAB_LABEL, STUDIO_TAB_ID, STUDIO_TAB_LABEL, pageAcceptsStudio, studioTabVisible,
} from '../studio/studio-gate';
import { StudioSkeletonComponent } from '../studio/studio-skeleton.component';
// The picker's entry in this page's tab set (FRAME_PICKER_DESIGN.md §6.1). The SAME shape as the
// Studio's three imports above, on purpose: a gate module that imports nothing, a URL module that
// validates its own tab id and owns its own keys, and a skeleton. Nothing here reaches the
// picker's runtime modules — those live in the chunk `PickerRoot` loads.
import { closePickerTab, isPickerTab, openPickerTab, readPickerUrl } from '../frame-picker/state/picker-url';
import {
    PICKER_TAB, PICKER_TAB_BADGE, PICKER_TAB_LABEL, pageAcceptsPicker, pickerTabVisible,
} from '../frame-picker/picker-gate';
import { PickerSkeletonComponent } from '../frame-picker/picker-skeleton.component';
import { compressNoGainPolicyEnabled, compressWavAutoDefaultEnabled, pdfCompressPresetsEnabled } from '../convert-options-v2/pdf-compress-presets.gate';
import { rerunDedupeOfferEnabled } from './rerun/rerun-offer';
import type { FramePickerHost } from '../frame-picker/host';
import { BOOT_VEIL_SCRIPT_ID, EDITOR_SKELETON_SELECTOR, bootVeilScript, watchBootVeil } from './boot-veil';

/**
 * The Studio body, in its own chunk, never evaluated during the static export.
 *
 * `ssr: false` is load-bearing twice over. (1) This file is statically imported by
 * `route-to-component-map.ts:1` and is therefore in the export entry graph;
 * `file-convert.component.tsx:813-822` records a webpack module-eval crash — one that tsc and
 * jest both pass — caused by pulling the inversify container into that graph, and the Studio
 * tree reaches both the container (its root resolves six services) and `MotiveComponent`.
 * (2) The editor is a browser thing: it probes WebGL, decodes images and reads localStorage, and
 * none of that has an answer during `next build`. The precedent is
 * `single-page-base.component.tsx:96-99,106-109`, which does exactly this for the same reason.
 *
 * Declared at module scope, never inside `render()`: `dynamic()` returns a NEW component each
 * call, so building it per render would unmount and remount the whole editor on every keystroke.
 */
const StudioRoot = dynamic(
    () => import('../studio/studio-root.component').then(m => ({ default: m.StudioRootComponent })),
    // `loading` is TYPED as a function component -- `(props: DynamicOptionsLoadingProps) =>
    // ReactNode` (next/dynamic/types.d.ts) -- so a class cannot be handed over directly even
    // though React renders one perfectly well; `tsc` rejects it with TS2322. The one-line adapter
    // keeps the skeleton itself a CLASS, which is the owner's ruling, without weakening the type
    // with a cast.
    { ssr: false, loading: () => <StudioSkeletonComponent /> },
);

/**
 * The Frame picker body, in its own chunk, never evaluated during the static export — the
 * Studio's reasons above apply verbatim (§6.1: "Body is `dynamic(() => import(...), { loading:
 * PickerSkeleton })`", precedent `single-page-base.component.tsx:21`). Module scope, one
 * component for the life of the page.
 */
const PickerRoot = dynamic(
    () => import('../frame-picker/frame-picker-root.component').then(m => ({ default: m.FramePickerRootComponent })),
    { ssr: false, loading: () => <PickerSkeletonComponent /> },
);


interface ConverterState {
    fileSelectBtnClicked: boolean;
    /** `CONVERT_TAB`, `STUDIO_TAB_ID` or `PICKER_TAB`. Driven by the URL, never by the click alone (§6.1). */
    activeTab: string;
    /** The page half of the Studio gate, narrowed later by the api-sourced flag (§6.1, D2). */
    studioVisible: boolean;
    /** The page half of the picker gate, narrowed later by `featureFlags.framePicker` (FRAME_PICKER §6.1). */
    pickerVisible: boolean;
    /**
     * The picker tab has been selected at least once on this page. From then on its body stays
     * MOUNTED and is only hidden (§6.1 mount-and-hide), so the playhead survives a hop to Quick
     * convert and back. Before then the chunk is not even requested: 518 pages carry the tab, and
     * a visitor who never opens it must not download an editor.
     */
    pickerMounted: boolean;
    /**
     * Compress PDF's two strong levels and its level copy (`pdf-compress-presets.gate.ts`):
     * `featureFlags.pdfBoxCompressor === true`. Starts false so the export and the hydrating
     * client agree, and only an emission that says `true` turns it on.
     */
    pdfCompressPresetsEnabled: boolean;
    /**
     * compress-wav's Auto codec and its note (the same gate): `featureFlags.compressNoGainPolicy === true`.
     * Starts false for the same reason; QA round 1 QA1-03 (it was marked in xconfig but never wired).
     */
    compressNoGainPolicyEnabled: boolean;
    /**
     * UXR1-01: `featureFlags.compressWavAutoDefault === true`. compress-wav shows Auto only with this AND
     * the no-gain policy on, because it is the flag ironman reads to deliver a "PCM 16-bit" pick as picked.
     */
    compressWavAutoDefaultEnabled: boolean;
    /** FIX-8 (`featureFlags.rerunDedupeOffer === true`); starts false like the PDF gate above. */
    rerunDedupeOffer: boolean;
}

interface ConverterProps extends RouteProps {

}


export default class ConverterPageBaseComponent extends Component<ConverterProps, ConverterState> {
    logger = Logger.getLogger('ConverterPageBaseComponent');

    private subscriptions: Subscription[] = [];

    el = createRef<HTMLDivElement>();
    @lazyInject(EnvironmentService) env: EnvironmentService;
    @lazyInject(LightHttp) lightHttp: LightHttp;
    @lazyInject(DOMStorage) domStorage: DOMStorage;
    @lazyInject(PageDataProviderService) pageDataProvider: PageDataProviderService;
    @lazyInject(MotivesService) motivesService: MotivesService;


    @lazyInject(UploadedFilesService) private uploadedFilesService: UploadedFilesService;
    @lazyInject(MotivesService) public motiveService: MotivesService;
    @lazyInject(SettingsService) private settingsService: SettingsService;
    @lazyInject(WindowService) private windowService: WindowService;

    showTestComponents = false;

    get domainname() {
        return this.env?.domainname;
    }
    get sitename() {
        return this.env?.sitename;
    }

    get showRating() {
        return this.env?.sitename === this.env?.env.constants.xconvertId;
    }

    get showInfoGrid() {
        return this.env?.sitename === this.env?.env.constants.xconvertId;
    }

    // public sitename = 'xconvert';
    public title: string;
    public description: string;
    /** FIX-6: the page's optional notice under the H1 (`page_seo.callout`); null on every page without one. */
    public callout: PageCallout | null = null;
    public features: Array<SeoFeature>;

    public showHTML = false;
    public isMobile: boolean;

    public feature;

    public markdownData;

    sidePanelFileUploads: FileUpload[];
    showSidePanelTriggered = false;
    canShowLivePreview = false;
    showLivePreview = false;

    effectiveMotives: MotiveConfig;
    extensionSeoDataList: ExtensionSEOData[] = [];

    featureDescriptionsDisabled = false;


    get internalAdsEnabled() {
        return this.effectiveMotives && this.effectiveMotives.internalMotivesEnabled;
    }
    get leftAdShown() {
        return this.effectiveMotives && this.effectiveMotives['convertPageLeft'] && !this.internalAdsEnabled;
    }
    get rightAdShown() {
        return this.effectiveMotives && this.effectiveMotives['convertPageRight'] && !this.internalAdsEnabled;
    }

    /**
     * `activeTab` starts on Quick convert and `studioVisible` on the PAGE half alone.
     *
     * Both have to be identical on the server and on the client or hydration mismatches: the
     * page half is a pure function of props, and the URL is not readable during the export, so
     * the tab is switched in `componentDidMount` (which is also where Back/Forward is wired).
     * The flag half only ever REMOVES the tab, so starting it true and letting the API take it
     * away is the owner's "visible by default" rule expressed as a state transition.
     */
    readonly state: Readonly<ConverterState> = {
        fileSelectBtnClicked: false,
        activeTab: CONVERT_TAB,
        studioVisible: pageAcceptsStudio(this.props?.pageData),
        pickerVisible: pageAcceptsPicker(this.props?.pageData, this.props?.convertOptionsV2),
        pickerMounted: false,
        pdfCompressPresetsEnabled: false,
        compressNoGainPolicyEnabled: false,
        compressWavAutoDefaultEnabled: false,
        rerunDedupeOffer: false,
    };

    /** The `onRouteChange` unsubscribe, held so a torn-down page stops following the URL. */
    private offRouteChange: (() => void) | null = null;
    private mounted = false;
    /** The latest verdict from `featureFlags$`, applied on mount and on every later emission. */
    private studioGateVerdict: boolean | null = null;
    /** The same, for the picker: `pickerTabVisible(pageData, convertOptionsV2, emission)`. */
    private pickerGateVerdict: boolean | null = null;
    /** The same, for Compress PDF's flag-gated levels: `pdfCompressPresetsEnabled(emission.flags)`. */
    private pdfPresetsGateVerdict: boolean | null = null;
    /** The same, for compress-wav Auto: `compressNoGainPolicyEnabled(emission.flags)`. */
    private noGainGateVerdict: boolean | null = null;
    /** The same, for UXR1-01: `compressWavAutoDefaultEnabled(emission.flags)`. */
    private wavAutoDefaultVerdict: boolean | null = null;
    /** The same, for FIX-8's duplicate-submission guard: `rerunDedupeOfferEnabled(emission.flags)`. */
    private rerunDedupeVerdict: boolean | null = null;

    /**
     * Is this a video→still page AT ALL — the page half of the picker gate, frozen for the page's
     * life. Decides the SHAPE of the tool container (Quick convert wrapped so it can be hidden, a
     * slot for the picker body) and never moves, so a flag flip changes what is visible and never
     * remounts `FileConvertComponent` — which owns the options context and the uploader, and
     * whose remount would drop every staged file (§0 "Placement").
     */
    private readonly pickerPage: boolean = pageAcceptsPicker(this.props?.pageData, this.props?.convertOptionsV2);

    /**
     * Review 7 (PAGE lane): the inline script that veils the wrapper on an editor deep link until the
     * editor has mounted (`boot-veil.ts` has the measurements and the why). Built from the PAGE half of
     * both gates -- props only -- so the export and the hydrating client emit the same string, and ''
     * (no script at all) on every page that offers neither editor.
     */
    private readonly bootVeilSource: string = bootVeilScript(
        [
            ...(pageAcceptsStudio(this.props?.pageData) ? [STUDIO_TAB_ID] : []),
            ...(this.pickerPage ? [PICKER_TAB] : []),
        ],
        styles['wrapper'] ? `.${styles['wrapper']}` : '',
    );
    /** Lifts the veil; set in `componentDidMount`, called again (idempotently) on unmount. */
    private stopBootVeil: (() => void) | null = null;

    /**
     * The three entries this page can offer, each with its id, label and pill. The STRIP is built
     * per render from the two gates (`tabSet()`), so it is empty on the ~5,000 pages that offer
     * neither editor, `[Quick convert, Studio]` on the 37 image→video pages and `[Quick convert,
     * Frame picker]` on the 518 video→still pages (§6.1: "the page owns ONE tab set").
     */
    private static readonly CONVERT_ENTRY: TabDefinition = { id: CONVERT_TAB, label: CONVERT_TAB_LABEL };
    private static readonly STUDIO_ENTRY: TabDefinition = { id: STUDIO_TAB_ID, label: STUDIO_TAB_LABEL };
    private static readonly PICKER_ENTRY: TabDefinition = { id: PICKER_TAB, label: PICKER_TAB_LABEL, badge: PICKER_TAB_BADGE };

    private tabSet(): TabDefinition[] {
        const tabs: TabDefinition[] = [ConverterPageBaseComponent.CONVERT_ENTRY];
        if (this.state.studioVisible) tabs.push(ConverterPageBaseComponent.STUDIO_ENTRY);
        if (this.state.pickerVisible) tabs.push(ConverterPageBaseComponent.PICKER_ENTRY);
        return tabs;
    }

    /** Any entry besides Quick convert — what decides whether a strip and a `tabpanel` render. */
    private get anyTabVisible(): boolean {
        return this.state.studioVisible || this.state.pickerVisible;
    }

    constructor(props: any) {
        super(props);
        this.pickerHost = this.buildPickerHost();
        this.effectiveMotives = this.motiveService.freeUserMotives;
        this.loadComponentData(this.props);
        this.updatePageData(props.pageData);
    }

    componentDidMount() {
        // Round 13 (R13-Z2, S2): a deep link may mount with an editor already selected -- the snap class follows.
        syncEditorSnap(this.fittedNow);
        const componentMountedTime = Date.now();
        const fileSelectSub = this.windowService.onFileSelectBtnClicked.pipe(
            filter(evt => evt.dispatchedAt.getTime() > componentMountedTime)
        ).subscribe({
            next: (data) => {
                if (!this.state.fileSelectBtnClicked) {
                    this.setState({
                        fileSelectBtnClicked: false,
                    });
                }
            }
        });
        this.subscriptions.push(fileSelectSub);

        // §6.4: `routeChangeComplete` + `pageshow`, and deliberately no `popstate` listener —
        // Next already handles popstate for the entries it wrote, and a second listener would
        // run this twice for one Back. `onRouteChange` is P5a's; it owns both halves.
        this.mounted = true;
        if (this.studioGateVerdict !== null && this.studioGateVerdict !== this.state.studioVisible) {
            this.setState({ studioVisible: this.studioGateVerdict });
        }
        if (this.pickerGateVerdict !== null && this.pickerGateVerdict !== this.state.pickerVisible) {
            this.setState({ pickerVisible: this.pickerGateVerdict });
        }
        if (this.pdfPresetsGateVerdict !== null && this.pdfPresetsGateVerdict !== this.state.pdfCompressPresetsEnabled) {
            this.setState({ pdfCompressPresetsEnabled: this.pdfPresetsGateVerdict });
        }
        if (this.noGainGateVerdict !== null && this.noGainGateVerdict !== this.state.compressNoGainPolicyEnabled) {
            this.setState({ compressNoGainPolicyEnabled: this.noGainGateVerdict });
        }
        if (this.wavAutoDefaultVerdict !== null && this.wavAutoDefaultVerdict !== this.state.compressWavAutoDefaultEnabled) {
            this.setState({ compressWavAutoDefaultEnabled: this.wavAutoDefaultVerdict });
        }
        if (this.rerunDedupeVerdict !== null && this.rerunDedupeVerdict !== this.state.rerunDedupeOffer) {
            this.setState({ rerunDedupeOffer: this.rerunDedupeVerdict });
        }
        this.offRouteChange = onRouteChange(this.syncTabFromUrl);
        this.syncTabFromUrl();
        // Review 7: lift the deep-link veil once the selected editor has replaced its skeleton and the
        // tool container has stopped growing (no-op when the inline script put no veil up).
        this.stopBootVeil = watchBootVeil({
            box: () => this.toolContainerRef.current,
            waiting: () => this.fittedNow && !!this.toolContainerRef.current?.querySelector(EDITOR_SKELETON_SELECTOR),
        });
    }

    loadComponentData(props) {
        this.feature = RouteUtils.getPathFromProps(props);
        this.isMobile = BrowserUtils.isMobile();
        this.domStorage.setBaseComponentReadyAndFreezeContentIfNeeded();

        this.motivesService.onEffectiveMotiveSettingsChange
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (motives) => {
                    this.effectiveMotives = motives;
                    // Preact disable this until find a better way. For now its going though the route data
                    // this.changeKeyAndForceUpdate();
                },
                error: (error) => {
                    this.logger.error({ message: 'Motive settings change observer threw an error', error });
                    // this.changeKeyAndForceUpdate();
                },
            });


        this.settingsService.userSettings
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (userSettings) => {
                    this.featureDescriptionsDisabled = get(userSettings, 'featureDescriptionsDisabled', false);
                },
            });
        // `featureFlags$`, not `featureFlags`: the gate has to tell "the API said off" from
        // "the build baked PROD's flag set into the page props" (settings.service.ts:28-33).
        // `showLivePreview` has no such requirement and keeps reading the bare flags off the
        // same emission, so this is one subscription rather than two.
        this.settingsService.featureFlags$
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (emission) => {
                    this.showLivePreview = get(emission?.flags, 'showLivePreview', false);
                    // `loadComponentData` runs from the CONSTRUCTOR and this subject is a
                    // ReplaySubject, so the first emission arrives synchronously — before React
                    // considers this component mounted. `setState` there is a warning and, on a
                    // client-side navigation where the API answer is already buffered, a real one.
                    // So the verdict is parked and applied in `componentDidMount`.
                    this.studioGateVerdict = studioTabVisible(this.props?.pageData, emission);
                    // The picker's half of the same emission, the same way (FRAME_PICKER §6.1:
                    // "subscribed for the page's lifetime, no deadline, hide only on an explicit
                    // false"). One subscription for three readers, not three subscriptions.
                    this.pickerGateVerdict = pickerTabVisible(this.props?.pageData, this.props?.convertOptionsV2, emission);
                    this.pdfPresetsGateVerdict = pdfCompressPresetsEnabled(emission?.flags);
                    this.noGainGateVerdict = compressNoGainPolicyEnabled(emission?.flags);
                    this.wavAutoDefaultVerdict = compressWavAutoDefaultEnabled(emission?.flags);
                    this.rerunDedupeVerdict = rerunDedupeOfferEnabled(emission?.flags);
                    if (!this.mounted) return;
                    if (this.studioGateVerdict !== this.state.studioVisible) {
                        this.setState({ studioVisible: this.studioGateVerdict });
                    }
                    if (this.pickerGateVerdict !== this.state.pickerVisible) {
                        this.setState({ pickerVisible: this.pickerGateVerdict });
                    }
                    if (this.pdfPresetsGateVerdict !== this.state.pdfCompressPresetsEnabled) {
                        this.setState({ pdfCompressPresetsEnabled: this.pdfPresetsGateVerdict });
                    }
                    if (this.noGainGateVerdict !== this.state.compressNoGainPolicyEnabled) {
                        this.setState({ compressNoGainPolicyEnabled: this.noGainGateVerdict });
                    }
                    if (this.wavAutoDefaultVerdict !== this.state.compressWavAutoDefaultEnabled) {
                        this.setState({ compressWavAutoDefaultEnabled: this.wavAutoDefaultVerdict });
                    }
                    if (this.rerunDedupeVerdict !== this.state.rerunDedupeOffer) {
                        this.setState({ rerunDedupeOffer: this.rerunDedupeVerdict });
                    }
                },
            });
    }

    /**
     * `props.url` is baked by the static export and does not move when the tab is pushed with
     * `shallow: true` (`_app.tsx` recomputes routeProps from the same props), so the tab has
     * to be state and this method has to know about it — without these two lines the Studio tab
     * writes the URL and nothing repaints. §6.1 says so; this is why.
     */
    shouldComponentUpdate(nextProp: ConverterProps, nextState: ConverterState) {
        const isDifferentURL = nextProp.url !== this.props.url;
        const isFileSelectBtnClickedStateChanged = nextState.fileSelectBtnClicked !== this.state.fileSelectBtnClicked;
        const isTabChanged = nextState.activeTab !== this.state.activeTab;
        const isGateChanged = nextState.studioVisible !== this.state.studioVisible
            || nextState.pickerVisible !== this.state.pickerVisible
            || nextState.pdfCompressPresetsEnabled !== this.state.pdfCompressPresetsEnabled
            || nextState.compressNoGainPolicyEnabled !== this.state.compressNoGainPolicyEnabled
            || nextState.compressWavAutoDefaultEnabled !== this.state.compressWavAutoDefaultEnabled
            || nextState.rerunDedupeOffer !== this.state.rerunDedupeOffer;
        const isPickerMountChanged = nextState.pickerMounted !== this.state.pickerMounted;
        return isDifferentURL || isFileSelectBtnClickedStateChanged || isTabChanged || isGateChanged || isPickerMountChanged;
    }

    /**
     * The URL is the one source of truth for which tab is showing (§6.4).
     *
     * THREE-WAY, and each entry validates its own id in its own module: `isStudioTab` over the
     * Studio's reading, `isPickerTab` over the picker's. There is no shared union and no shared
     * `readEditorTab` — the owner's no-coupling rule (FRAME_PICKER §0 "Tab state"). A `tab` that
     * neither claims is Quick convert.
     */
    private syncTabFromUrl = () => {
        let tab = CONVERT_TAB;
        if (isStudioTab(readStudioUrl().tab)) tab = STUDIO_TAB_ID;
        else if (isPickerTab(readPickerUrl().tab)) tab = PICKER_TAB;
        if (tab !== this.state.activeTab) this.setState(this.tabPatch(tab));
    };

    /**
     * The state patch for a tab change. Selecting the picker also latches `pickerMounted`, which
     * never goes back to false on this page (see the field's KDoc).
     */
    private tabPatch(next: string): Pick<ConverterState, 'activeTab' | 'pickerMounted'> {
        return { activeTab: next, pickerMounted: this.state.pickerMounted || next === PICKER_TAB };
    }

    /**
     * A tab click writes the URL and paints optimistically.
     *
     * The write is what matters — `syncTabFromUrl` reconciles on `routeChangeComplete` and wins
     * any disagreement. The optimistic `setState` exists because a router push is asynchronous
     * and a tab that highlights one frame late reads as a dropped click.
     *
     * Opening pushes `{tab}` only: the page base does not know which project is being opened
     * (that answer lives in `state/persistence.ts`, which must not reach this file's import
     * graph — D10), and `StudioTabComponent` REPLACES `{sid}` as soon as it does. Two pushes for
     * one click would leave Back inside the Studio. The picker is the same: `openPickerTab()`
     * with no session writes `{tab}` alone and the picker root replaces `{psid}`.
     * Closing uses the LEAVING tab's own helper — `closeStudioTab` clears `tab`, `sid`, `step`
     * and `job`; `closePickerTab` clears `tab`, `psid` and `pstep` — so neither editor ever
     * writes or clears the other's keys (FRAME_PICKER §6.4).
     */
    private onSelectTab = (id: string) => {
        let next = CONVERT_TAB;
        if (isStudioTab(id)) next = STUDIO_TAB_ID;
        else if (isPickerTab(id)) next = PICKER_TAB;
        if (next === this.state.activeTab) return;
        if (next === STUDIO_TAB_ID) writeTabParam(STUDIO_TAB_ID);
        else if (next === PICKER_TAB) openPickerTab();
        else if (this.state.activeTab === PICKER_TAB) closePickerTab();
        else closeStudioTab();
        this.setState(this.tabPatch(next));
    };

    /**
     * The Studio's own "leave" affordance. A bound property, not an inline arrow in `render()`:
     * a fresh closure per render would re-render the whole editor for nothing.
     */
    private closeStudio = () => this.onSelectTab(CONVERT_TAB);

    /** The tab is showing AND the page still offers it — a flag flip mid-session falls back. */
    private get studioSelected(): boolean {
        return this.state.studioVisible && this.state.activeTab === STUDIO_TAB_ID;
    }

    /** The same rule for the picker: `?tab=picker` with the flag off falls back to Quick convert silently (§6.1). */
    private get pickerSelected(): boolean {
        return this.state.pickerVisible && this.state.activeTab === PICKER_TAB;
    }

    private updatePageData(value: PageData) {

        this.features = value.page_seo.feature;
        this.title = value.page_seo.title;
        this.description = value.page_seo.description && value.page_seo.description.replace(/\s+/g, ' ');
        this.callout = pageCalloutOf(value.page_seo);
        this.uploadedFilesService.initializeForCompatibility(value.fromType, value.accept);
        this.uploadedFilesService.notyfyAll();

        this.updateCanShowLivePreview(value.fromType, value.toType);
    }

    componentWillUnmount() {
        this.mounted = false;
        if (this.stopBootVeil) { this.stopBootVeil(); this.stopBootVeil = null; }
        syncEditorSnap(false);
        if (this.offRouteChange) { this.offRouteChange(); this.offRouteChange = null; }
        if (this.pickerResizeSub) { this.pickerResizeSub.unsubscribe(); this.pickerResizeSub = null; }
        // for untilDestroyed
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    updateCanShowLivePreview(fromType: FromType[], toType: ToType) {
        const notAllowedToTypes = [ToTypes.VIDEO]; // []; //
        const notAllowedFromTypes = [FromTypes.VIDEO, FromTypes.AV]; // []; //
        if (notAllowedToTypes.indexOf(toType) === -1 && !notAllowedFromTypes.find(notAllowedFromType => fromType.indexOf(notAllowedFromType) > -1)) {
            this.canShowLivePreview = true;
        } else {
            this.canShowLivePreview = false;
        }
    }

    public navigateToPage(pageHeaderLink: any) {
        if (pageHeaderLink.type === 'remote') {
            window.location.href = pageHeaderLink.url;
        }
        // TODO Preact fix
        // this.router.navigate([pageHeaderLink.url]);
    }

    /**
     * `studioVisible` is handed DOWN rather than recomputed below.
     *
     * The Quick convert tab offers the Studio when a pick is two or more still photos (§6.1), and
     * that offer must be true of the same two facts the tab strip is: the page AND an api-sourced
     * `featureFlags.studio` that is not `false`. This component is the one that holds both — it is
     * the only subscriber to `featureFlags$` on the converter page — so the child reads the
     * verdict instead of deriving half of it. Deriving half of it is exactly the defect: with the
     * §11.1 step 3 dark write in place (`studio:false` on prod BEFORE the web deploy), a banner
     * gated on the PAGE alone would offer a destination that renders nothing, and would count the
     * offer in NUDGE_SHOWN.
     */
    getFIleConvertComponentHtml(props: ConverterProps) {
        // §0c condition 1: the ad slots gate on the TAB BEING ACTIVE, never on `hidden`. A hidden
        // Quick convert body must render no ad unit — an impression nobody can see is invalid
        // traffic — so the ELEMENTS are withheld, not merely concealed.
        const quickConvertActive = !this.pickerSelected;
        const body = <FileConvertComponent
            {...props}
            ref={this.fileConvertRef}
            studioVisible={this.state.studioVisible}
            pickerVisible={this.state.pickerVisible}
            pickerActive={this.pickerSelected}
            pdfCompressPresetsEnabled={this.state.pdfCompressPresetsEnabled}
            compressNoGainPolicyEnabled={this.state.compressNoGainPolicyEnabled}
            compressWavAutoDefaultEnabled={this.state.compressWavAutoDefaultEnabled}
            rerunDedupeOffer={this.state.rerunDedupeOffer}
            onStudioHandoff={this.onStudioHandoff}
            belowToolAd={quickConvertActive ? this.belowToolAd() : undefined}
            aboveToolAd={quickConvertActive ? this.aboveToolAd() : undefined}>
        </FileConvertComponent>;
        // Only a video→still page gets the wrapper, and it gets it for the page's whole life
        // (`pickerPage` is frozen), so the element type at this position never changes and the
        // component under it — the owner of the options context and the uploader — is never
        // remounted by a tab switch or a flag flip. Every other converter renders exactly as before.
        if (!this.pickerPage) return body;
        return <div hidden={!quickConvertActive} data-testid="quick-convert-body">{body}</div>;
    }

    /** The Quick convert instance, whose `host` the picker consumes (FRAME_PICKER §6.1). */
    private readonly fileConvertRef = createRef<FileConvertComponent>();

    /**
     * The host handed to the picker root — ONE object for the page's life, delegating to whatever
     * `FileConvertComponent` is mounted. Stable identity is the point: the root subscribes to
     * `host.stagedFiles$` and `host.onConverted$` on mount, and a host rebuilt per render would
     * hand it dead observables. The getters resolve late because the child's ref is null during
     * the page's first render, and the picker body is never rendered before that render commits
     * (`pickerMounted` latches on a tab selection, which is after mount). Built in the
     * constructor (`buildPickerHost`) so the getters can close over `this`.
     */
    private readonly pickerHost: FramePickerHost;

    private buildPickerHost(): FramePickerHost {
        const child = (): FramePickerHost => {
            const mounted = this.fileConvertRef.current;
            if (!mounted) throw new Error('FramePickerHost used before FileConvertComponent mounted');
            return mounted.host;
        };
        return {
            get optionsContext() { return child().optionsContext; },
            // R9b (additive): the page's accepted list and the uploader's refusals, both Quick convert's own.
            get acceptedExtensions() { return child().acceptedExtensions; },
            get rejected$() { return child().rejected$; },
            get stagedFiles$() { return child().stagedFiles$; },
            // Both throw the same "used before FileConvertComponent mounted" error when the page is
            // not there (a Back remount mid-load); the picker root catches it into a notice (R9b),
            // so the failure is never silent.
            stageFiles: (files, source) => child().stageFiles(files, source),
            convertFiles: (files) => child().convertFiles(files),
            get onConverted$() { return child().onConverted$; },
            // R14 (picker slice J, carried after R14-PC2): the picker's Remove on an unreadable row takes the upload off
            // THIS page's list too -- the editor's own `removeFile(file)`, the path the file card's Remove key takes, found
            // by uuid on the editor's list. False (not thrown) when the page or the file is not there: the picker's local
            // dismissal stands on its own.
            removeFile: (uuid) => {
                const editor = this.fileConvertRef.current?.editorRef.current;
                const hit = editor?.getFiles().find((f) => f?.uuid === uuid);
                if (!editor || !hit) return false;
                editor.removeFile(hit.getFile());
                return true;
            },
        };
    }

    /**
     * The picker body, mounted on first selection and HIDDEN thereafter rather than unmounted
     * (§6.1 mount-and-hide). `active` tells the root which state it is in; the root re-reads the
     * options summary on activation because the context's emit is idle-deferred (§6.5).
     *
     * While SELECTED the wrapper is the picker's fitted box (picker-fix F2, Q10): on the desktop
     * it is `100dvh` minus its own top offset (`--xc-picker-top`, published by
     * `publishPickerTop`) minus the anchor bar, a flex column the root fills, and nothing inside
     * it grows the page -- the Output column scrolls internally (`.picker-fitted`,
     * converter-page-base.component.module.scss). The class is added only while selected so the
     * hidden wrapper's `display: none` is never fought.
     */
    getPickerHtml() {
        const selected = this.pickerSelected;
        const className = selected ? `${styles['picker-body']} ${styles['picker-fitted']}` : styles['picker-body'];
        return <div ref={this.pickerBodyRef} className={className} hidden={!selected} data-testid="frame-picker-body">
            <PickerRoot host={this.pickerHost} active={selected} />
        </div>;
    }

    /** The picker wrapper, measured for `--xc-picker-top` (see `getPickerHtml`). */
    private readonly pickerBodyRef = createRef<HTMLDivElement>();
    private pickerResizeSub: Subscription | null = null;

    /**
     * Publish the picker wrapper's top offset while it is selected, and keep it fresh on resize.
     * A hidden wrapper measures 0, so the write happens only on the selected render; the
     * subscription lives exactly as long as the selection does.
     */
    private publishPickerTop(): void {
        publishFittedTop(this.pickerBodyRef.current, PICKER_TOP_VAR);
    }

    private syncPickerTopWatcher(): void {
        if (this.pickerSelected) {
            this.publishPickerTop();
            if (!this.pickerResizeSub) {
                const resize$ = this.windowService?.onResize;
                if (resize$) this.pickerResizeSub = resize$.subscribe(() => this.publishPickerTop());
            }
        } else if (this.pickerResizeSub) {
            this.pickerResizeSub.unsubscribe();
            this.pickerResizeSub = null;
        }
    }

    /**
     * The fitted editors follow the selection: the picker wrapper's variable and its resize watcher
     * (picker pages only), and -- for BOTH editors -- the body's scroll-snap class (`syncEditorSnap`).
     *
     * Round 13 (R13-Z2, owner review 5 S2/P6): NO programmatic scroll on activation. Rounds 3-12 ran
     * `claimViewport` here so the site nav left the screen the moment a tab became selected; the owner:
     * "don't auto scroll -- this would cost us Core Web Vitals, specially the SSR build with a
     * `?tab=studio&sid=…` URL, which jumps". The page now keeps its header, the editor is one viewport
     * tall under it, and the PAGE SCROLLER snaps the editor to the top (`scroll-snap-type: y proximity`
     * on `body.xc-editor-snap`, `scroll-snap-align: start` on the tool container) as the user scrolls.
     * `document.body.scrollTop` is therefore untouched by a tab open, a deep link or a restore -- the
     * e2e reads it. "Fit to screen" (the Studio shell's explicit pin) stays the one way to pin.
     */
    componentDidUpdate(_prevProps: ConverterProps, prevState: ConverterState) {
        const selectionMoved = prevState.activeTab !== this.state.activeTab
            || prevState.pickerVisible !== this.state.pickerVisible
            || prevState.pickerMounted !== this.state.pickerMounted
            || prevState.studioVisible !== this.state.studioVisible;
        if (!selectionMoved) return;
        if (this.pickerPage) this.syncPickerTopWatcher();
        syncEditorSnap(this.fittedNow);
    }

    /** The tool container: the Studio renders inside it, so it is the box the page scroller snaps to. */
    private readonly toolContainerRef = createRef<HTMLDivElement>();

    /**
     * §6.1 / S-02 / picker-fix F2 Q10: an editor tab is selected AND its editor is mounted, so the page
     * furniture above it (H1, subtitle, tab strip, the wrapper's and the card's padding) leaves the layout
     * and the editor can be one viewport tall. One getter, so `render`, the snap class and the probes
     * agree on what "fitted" is.
     */
    private get fittedNow(): boolean {
        const workspace = isWorkspaceActive({
            requested: this.state.activeTab === STUDIO_TAB_ID || this.state.activeTab === PICKER_TAB,
            editorMounted: this.state.activeTab === STUDIO_TAB_ID ? this.state.studioVisible : this.state.pickerVisible,
        });
        return workspace && (this.studioSelected || this.pickerSelected);
    }

    /**
     * The Studio, handed the page's data and the page's verdict on ads.
     *
     * The ad ELEMENTS are not passed down here, unlike `belowToolAd`/`aboveToolAd` above.
     * That rule exists to keep the inversify container out of this file's import graph
     * (`file-convert.component.tsx:813-822`), and the Studio's two units live in the lazily
     * imported chunk where that edge is already paid for. What the page keeps is the DECISION:
     * `adsEnabled` is the page's own `internalAdsEnabled` getter, so an internal-ads page shows
     * no AdSense rectangle inside the editor either.
     */
    getStudioHtml() {
        return <StudioRoot
            pageData={this.props.pageData}
            convertOptionsV2={this.props.convertOptionsV2}
            path={this.feature}
            adsEnabled={!this.internalAdsEnabled}
            handoff={this.studioHandoff}
            onClose={this.closeStudio}
        />;
    }

    /**
     * The nudge's photos, on their way from Quick convert to the Studio (§6.1, final review I-1).
     *
     * A FIELD and not state, for two reasons. It must be readable by the very next render — the
     * accept hands the files over and then writes `?tab=studio`, and the router hop is what mounts
     * the editor — so a `setState` here would be a second render for nothing. And it is not a fact
     * about how this page LOOKS: nothing above the Studio root renders from it.
     *
     * Typed `any[]` at this boundary deliberately, exactly as `pageData` is: the page base is in
     * the static entry graph of all 37 pages, and `UploadInProgressFile` is a model the converter
     * already carries there — but naming the Studio's own types here would be the first edge from
     * the entry graph into `components/studio/`, which is the whole of D10.
     */
    private studioHandoff: { uploads: any[]; changes?: any; source: string } | null = null;

    private onStudioHandoff = (handoff: { uploads: any[]; changes?: any; source: string }) => {
        // Kept whole and kept even when it carries no files: `source` is what tells the editor it
        // was opened by the offer rather than by a tab click (§8), and the editor decides for
        // itself whether there is anything to adopt.
        this.studioHandoff = handoff || null;
    };

    /**
     * The tab strip, on the pages that get one (37 image→video, 518 video→still).
     *
     * `idPrefix` and NOT `panelIdPrefix`: `aria-controls` would have to name one panel element
     * per tab, and this page has a single tool container that swaps its contents. The leaf's own
     * KDoc says a WRONG `aria-controls` is worse than none, so the relationship is expressed the
     * other way round — the container carries `role="tabpanel"` and `aria-labelledby` pointing at
     * whichever tab is selected (see `render`).
     *
     * `testId` keeps the Studio's name: the Studio's e2e rows and its spec read it, and a strip
     * is a strip whichever second entry it carries.
     *
     * `collapsed` (S-02): while the Studio is open the strip is HIDDEN, not unmounted -- its job
     * is done by the "← Quick convert" button in the Studio header's left cluster, and the mock
     * (`mocks/Main.png`) has no strip above the editor. Mount-and-hide rather than a ternary for
     * the same reason the picker body is: the leaf keeps its state and the element type at this
     * position never changes, so a tab hop cannot remount anything under it.
     */
    getTabStripHtml(collapsed = false) {
        return <div className="mb-3" data-testid="converter-tab-strip" hidden={collapsed}>
            <TabStripComponent
                tabs={this.tabSet()}
                selectedId={this.state.activeTab}
                onSelect={this.onSelectTab}
                label="Choose how to convert"
                idPrefix="converter-tab"
                testId="studio-tab-strip"
            />
        </div>;
    }



    // shouldComponentUpdate(nextProps, t2) {
    //     if (this.props.path !== nextProps.path) {
    //         this.componentWillUnmount();
    //         this.loadComponentData(nextProps);
    //         this.updateKey();
    //     }
    //     return true;
    // }

    changeKeyAndForceUpdate() {
        this.updateKey();
        this.forceUpdate();
    }

    updateKey() {
        // this.setState({
        //     componentKey: StringUtils.getRandomString('key_'),
        // });
    }

    getRatingValue(rating: any): string {
        const total = (rating.rating_1 || 0) + (rating.rating_2 || 0) + (rating.rating_3 || 0) + (rating.rating_4 || 0) + (rating.rating_5 || 0);
        if (!total) return '4.5';
        return ((1*(rating.rating_1||0) + 2*(rating.rating_2||0) + 3*(rating.rating_3||0) + 4*(rating.rating_4||0) + 5*(rating.rating_5||0)) / total).toFixed(1);
    }

    formatReviewCount(rating: any): string {
        const total = (rating.rating_1 || 0) + (rating.rating_2 || 0) + (rating.rating_3 || 0) + (rating.rating_4 || 0) + (rating.rating_5 || 0);
        return total >= 1000 ? `${(total / 1000).toFixed(1).replace(/\.0$/, '')}K reviews` : `${total} reviews`;
    }

    /**
     * The banner that fills the empty file-list area above the uploader.
     *
     * `.sortable-container` reserves `min-height: 100px` for the staged-file list, and that
     * space is empty on every first visit -- a measured 128px hole between the description and
     * the drop zone on a phone. A 320x100 unit rendered *inside* that container occupies space
     * the layout was already spending, so the uploader does not move: putting it above the
     * container instead pushed the drop zone from y=363 to y=483, which is the exact harm an
     * ad above the tool normally does.
     *
     * Only while nothing is staged; the moment there is work in progress the list needs the
     * room back. docs/proposed/AD_PLACEMENT_DESIGN.md 9
     */
    aboveToolAd() {
        return <MotiveComponent
            data={AdSlots.pageTop}
            containerWidth="100%"
            containerHeight="100px"
            location={MotiveLocationTypes.convertPageTop}
            adWidth="320px"
            adHeight="100px"
            showAds={true}
            className='width_major'
        ></MotiveComponent>;
    }

    /**
     * The ad that sits directly under the uploader, rendered here and handed down.
     *
     * Never above the tool -- that placement pushed the thing the visitor came for down the
     * page, which is the leak this workstream exists to close. But "after the whole tool
     * container" put it below the Advanced Options accordion, and on a phone that makes the
     * container 1,168px tall, so the first ad landed at y=1,629 -- nearly two screens down, and
     * a mobile visitor saw none at all.
     *
     * The location key keeps its old name so the server-side kill switch in settings.motive
     * keeps working without a migration. docs/proposed/AD_PLACEMENT_DESIGN.md 9
     */
    belowToolAd() {
        // Leaderboard/banner (not the 336x280 mobile rectangle) — owner preference
        // 2026-09-08: the below-tool position should read as a slim banner on every
        // width. width_major = 320x100 -> 468x60 -> 728x90 -> 970x90 (no rectangle).
        return <div className="feature-below-tool d-flex justify-content-center mt-3">
            <MotiveComponent
                data={AdSlots.belowTool}
                containerWidth="100%"
                containerHeight="100px"
                location={MotiveLocationTypes.convertPageBelowFeatureTitle}
                adWidth="970px"
                adHeight="90px"
                showAds={true}
                className='width_major'
            ></MotiveComponent>
        </div>;
    }

    getSEOContent() {
        return <div className={`row ${styles['seo-container']}`}>
            {
                <div className="col-12 mt-3" >
                    <FeatureDetailsComponent markdown={this.props.markdown}></FeatureDetailsComponent>
                </div>
            }
            {/*
              * The only ad inside the SEO body, at the boundary after the long-form content
              * rather than spliced into the prose.
              *
              * Measured on prod: a converter page runs to ~7,500px and Auto Ads leaves
              * everything between the tool and the footer empty -- thousands of pixels a reader
              * scrolls past with nothing in them. This is the premium, non-blocking half of the
              * page: the visitor has finished their job by the time they reach it, so an ad here
              * costs no task time, and it is roughly 3,000px clear of the units above and below
              * it.
              *
              * Ships dark: `convertPageInContent` is absent from the settings document, so it
              * resolves falsy. Enable it only once Auto Ads is scoped off these URLs.
              * docs/proposed/AD_PLACEMENT_DESIGN.md 8.
              */}
            <div className="col-12 mt-3 d-flex justify-content-center">
                <MotiveComponent
                    data={AdSlots.inContent}
                    containerWidth="100%"
                    containerHeight="90px"
                    location={MotiveLocationTypes.convertPageInContent}
                    adWidth="970px"
                    adHeight="90px"
                    showAds={true}
                    className='width_major'
                ></MotiveComponent>
            </div>
            {
                <div className="col-12 mt-3" >
                    <RelatedLinksComponent relatedLinks={this.props.relatedLinks}></RelatedLinksComponent>
                </div>
            }
            {
                this.showRating && <div className="col-12 mt-3" >
                    <RatingDisplayComponent
                        rating={this.props.rating}
                        title={this.title}
                        feature={this.feature}
                        description={this.description}>
                    </RatingDisplayComponent>
                </div>
            }
            {
                this.showInfoGrid && !this.featureDescriptionsDisabled && <div className="col-12 mt-3">
                    {
                        this.features && <FeaturesTilesComponent features={this.features}></FeaturesTilesComponent>
                    }
                </div>
            }
            {
                <div className="col-12 mt-3" >
                    <ExtensionInfoComponent {...this.props}></ExtensionInfoComponent>
                </div>
            }
        </div>;
    }

    render() {
        // §6.1: the full-bleed shell is the EXISTING workspace-mode mechanism, not a third
        // modifier. `requested` is the tab the user chose; `editorMounted` is whether the page
        // still offers it at all, so a flag flip with the Studio open drops the chrome in the
        // same tick the editor is replaced (the rule `single-page-base.component.tsx:443-448`
        // states in prose). §0c condition 2: `workspaceWrapperStyle(true)` ONLY while an EDITOR
        // tab — the Studio's or the picker's — is active; the closed-tab render is byte-identical
        // (`converter-page-base.studio-tab.spec.tsx` and the three-fixture spec both pin it).
        const workspace = isWorkspaceActive({
            requested: this.state.activeTab === STUDIO_TAB_ID || this.state.activeTab === PICKER_TAB,
            editorMounted: this.state.activeTab === STUDIO_TAB_ID ? this.state.studioVisible : this.state.pickerVisible,
        });
        // S-02 (R1): the Studio is a FITTED editor -- the page furniture above it (H1, subtitle,
        // tab strip, the wrapper's and the card's padding) leaves the layout so the editor can be
        // one viewport tall. The picker is fitted the same way (picker-fix F2, Q10: with the
        // furniture kept the editor started at y=292 and fitted no viewport; mock `Main.png` has
        // the header bar at the very top).
        const fitted = this.fittedNow;
        const anyTabVisible = this.anyTabVisible;
        return <MotiveFrame hideSidePanes={workspace}>
            {/* Review 7: runs while the export's HTML is parsed, BEFORE the wrapper below exists, so an
                editor deep link never paints the Quick convert layout it is about to rearrange. */}
            {this.bootVeilSource && <script id={BOOT_VEIL_SCRIPT_ID} dangerouslySetInnerHTML={{ __html: this.bootVeilSource }} />}
            <div className={styles['wrapper']} style={fitted ? fittedWrapperStyle() : undefined}>
                {/*
                  * `workspaceWrapperStyle` is applied ONLY while the workspace is open. Its
                  * inactive branch is `maxWidth: 1200px` — `single-page-base`'s number — and this
                  * card is 1000px (converter-page-base.component.module.scss:16-31), so applying
                  * it unconditionally would widen every converter page on the site by 200px with
                  * the Studio closed. The spec asserts the closed render carries no inline width.
                  */}
                <div
                    className={workspace ? `${styles['main-card']} ${styles[WORKSPACE_BODY_CLASS]}` : styles['main-card']}
                    style={fitted ? fittedCardStyle() : workspace ? workspaceWrapperStyle(true) : undefined}
                >
                    <div className={`${styles['convert-page']} ${this.state.fileSelectBtnClicked ? styles['file-selected'] : ''}`}>
                        {/* Hidden, not unmounted, while the Studio is open (S-02): the H1 stays in
                            the document for the crawler and the closed render is byte-identical. */}
                        <div className={styles['feature-details']} hidden={fitted}>
                            <h1 className={`${styles['title']} text-center`}>
                                {this.title}
                            </h1>
                            <div className={`${styles['description']} secondary-text text-center`}
                                dangerouslySetInnerHTML={{ __html: this.description }}></div>
                            {this.callout && <div className={styles['page-callout']} role="note" data-jkey="page-callout">
                                <span aria-hidden="true">ⓘ </span>
                                <span className="sr-only">Note: </span>
                                <TextWithLink text={this.callout.text} link={this.callout.link}
                                    className={styles['page-callout-link']} jkey="page-callout-link" />
                            </div>}
                        </div>
                        {anyTabVisible && this.getTabStripHtml(fitted)}
                        {/*
                          * The Studio renders IN PLACE OF the tool, inside the same container, so
                          * the SEO body and the above-footer unit keep their order and the page's
                          * outline is unchanged whichever tab is open (§6.1). The picker renders
                          * BESIDE the tool — Quick convert stays mounted and hidden under it, so its
                          * options context, uploader and editor survive and the picker rides them
                          * through the host (FRAME_PICKER §6.1). The Studio ternary never fires on
                          * a picker page: `pageAcceptsStudio` is toType-video only.
                          *
                          * The tabpanel role appears only when the strip does — for EITHER entry: a
                          * `role="tabpanel"` with no tablist on the page is worse markup than none,
                          * and every converter that offers no editor must render exactly as before.
                          */}
                        <div
                            ref={this.toolContainerRef}
                            /* Round 13 (R13-Z2, S2): the fitted editor's box is the page scroller's snap
                               target (`scroll-snap-align: start`); the closed render carries the plain class. */
                            className={fitted ? `${styles['tool-container']} ${styles['tool-container-fitted']}` : styles['tool-container']}
                            data-testid="tool-container"
                            role={anyTabVisible ? 'tabpanel' : undefined}
                            aria-labelledby={anyTabVisible ? `converter-tab-${this.state.activeTab}` : undefined}
                        >
                            {
                                this.studioSelected
                                    ? this.getStudioHtml()
                                    : this.getFIleConvertComponentHtml(this.props)
                            }
                            {this.pickerPage && this.state.pickerVisible && this.state.pickerMounted && this.getPickerHtml()}
                        </div>
                        {/*
                          * Round 8 (K2, O12; the owner: "the page's bottom SEO content should be there
                          * on both views"): the long-form body renders BELOW the editor in fitted mode
                          * too -- it used to be `display: none` under `.workspace-open`. The wrapper is
                          * the `studio-page-content` slot the editors' probes address; the page scrolls
                          * to it, which is exactly what the phone header's "Fit to screen" key exists
                          * to opt out of. Present in every render, so the closed render is unchanged.
                          */}
                        <div className={styles['page-content']} data-testid="studio-page-content" data-fitted={fitted ? 'true' : undefined}>
                            {this.getSEOContent()}
                        </div>
                        {showPageFurniture(workspace) && <div className="feature-above-footer d-flex justify-content-center">
                            <MotiveComponent
                                data={AdSlots.aboveFooter}
                                containerWidth="100%"
                                containerHeight="90px"
                                location={MotiveLocationTypes.convertPageBottom}
                                adWidth="970px"
                                adHeight="90px"
                                showAds={true}
                                className='width_major'
                            ></MotiveComponent>
                        </div>}
                    </div>
                </div>
            </div>
        </MotiveFrame>
    }
}
